import { ChevronLeft } from "lucide-react";
import { Spinner } from "react-bootstrap";

import { formatUploadHistoryCardDate } from "../utils/ehrUploadHistory";
import classes from "./EhrImportDetailsDrawer.module.css";

const SummaryRow = ({ label, tone = "default", value }) => (
	<div className={classes.summaryRow}>
		<span className={classes.summaryRowLabel}>{label}</span>
		<span className={`${classes.summaryRowValue} ${classes[`summaryTone${tone}`] || ""}`.trim()}>
			{Number(value || 0).toLocaleString("en-US")}
		</span>
	</div>
);

const EhrImportDetailsDrawer = ({ details, error, isLoading, onBack, onClose }) => {
	const validationSummary = details?.validationSummary;
	const issueCount = validationSummary?.issueCount || 0;

	return (
		<div className={classes.overlay} onClick={onClose} role="presentation">
			<aside
				aria-labelledby="import-details-title"
				aria-modal="true"
				className={classes.drawer}
				onClick={(event) => event.stopPropagation()}
				role="dialog">
				<div className={classes.header}>
					<button className={classes.backButton} onClick={onBack} type="button">
						<ChevronLeft aria-hidden="true" size={16} strokeWidth={2} />
						Back to History
					</button>
					{details?.displayId ? (
						<>
							<span aria-hidden="true" className={classes.headerDivider} />
							<h2 className={classes.headerTitle} id="import-details-title">
								{details.displayId}
							</h2>
						</>
					) : null}
				</div>

				<div className={classes.body}>
					{isLoading ? (
						<div className={classes.stateBlock}>
							<Spinner animation="border" size="sm" style={{ color: "#064260" }} />
							<p>Loading import details...</p>
						</div>
					) : error ? (
						<div className={classes.stateBlock}>
							<p className={classes.errorText}>{error}</p>
						</div>
					) : details ? (
						<>
							<dl className={classes.metaList}>
								<div className={classes.metaRow}>
									<dt>Filename</dt>
									<dd>{details.fileName || "—"}</dd>
								</div>
								<div className={classes.metaRow}>
									<dt>Uploaded Date</dt>
									<dd>{formatUploadHistoryCardDate(details.uploadedAt)}</dd>
								</div>
								<div className={classes.metaRow}>
									<dt>Uploaded By</dt>
									<dd>{details.uploadedByName || "—"}</dd>
								</div>
							</dl>

							{validationSummary ? (
								<div className={classes.validationCard}>
									<div className={classes.validationHeader}>
										<h3 className={classes.validationTitle}>Validation Results Summary</h3>
										{issueCount > 0 ? (
											<span className={classes.issueBadge}>
												{issueCount} Issue{issueCount === 1 ? "" : "s"}
											</span>
										) : null}
									</div>

									<div className={classes.summaryList}>
										<SummaryRow label="Verified (Clean)" tone="Success" value={validationSummary.verifiedClean} />
										<SummaryRow
											label="Matched with Issues"
											tone="Warning"
											value={validationSummary.matchedWithIssues}
										/>
										<SummaryRow label="Unconfirmed" tone="Warning" value={validationSummary.unconfirmed} />
										<SummaryRow label="Data Mismatch" tone="Danger" value={validationSummary.dataMismatch} />
										<SummaryRow
											label="Missing in TeleTrace"
											tone="Danger"
											value={validationSummary.missingInTeleTrace}
										/>
										<SummaryRow
											label="Unable to Validate"
											tone="Muted"
											value={validationSummary.unableToValidate}
										/>
									</div>

									<div className={classes.summaryTotalRow}>
										<span className={classes.summaryTotalLabel}>Total Records</span>
										<span className={classes.summaryTotalValue}>
											{Number(validationSummary.totalRecords || 0).toLocaleString("en-US")}
										</span>
									</div>

									{details.reconciles === false ? (
										<p className={classes.reconcileWarning}>
											Validation counts do not match processed import records for this run.
										</p>
									) : null}
								</div>
							) : (
								<div className={classes.pendingPanel}>
									<p>Validation results will appear once this import completes processing.</p>
								</div>
							)}

							{details.errorMessage ? (
								<p className={classes.errorText}>{details.errorMessage}</p>
							) : null}
						</>
					) : null}
				</div>
			</aside>
		</div>
	);
};

export default EhrImportDetailsDrawer;
